import React from 'react';
import { Link } from 'react-router-dom';
import { FiShield, FiTool, FiBook, FiArrowUpRight, FiFlag } from 'react-icons/fi';
import Reveal from '../fx/Reveal';
import CascadeGrid from '../fx/CascadeGrid';

const services = [
    {
        icon: <FiTool size={28} />,
        title: 'Cybersecurity Training',
        tag: 'on-prem · hybrid',
        description:
            'On-premise cyber labs with hands-on training in CTF, incident response, red teaming, and VAPT.',
        items: [
            'Jeopardy & Attack-Defense CTFs',
            'Table Top Exercises',
            'Incident Handling Training',
            'Boot2Root Challenges',
        ],
        to: '/services/cybersecurity-training',
        external: false,
    },
    {
        icon: <FiFlag size={28} />,
        title: 'CTF Organizer',
        tag: 'turn-key · enterprise',
        description:
            'End-to-end Capture The Flag operations for universities, government, and enterprises.',
        items: [
            'Custom problem set design',
            'Live scoreboard & attack-defense infra',
            'On-site marshalling & ops crew',
            'Judging, prizes, livestream',
        ],
        to: '/services/ctf-vendor',
        external: false,
        highlight: true,
    },
    {
        icon: <FiShield size={28} />,
        title: 'Red Team & Pentest',
        tag: 'engagement-based',
        description:
            'Adversary-emulation, vulnerability assessment & penetration testing for orgs of all sizes.',
        items: [
            'Web Application Pentesting',
            'Mobile App Security Testing',
            'API Security Assessment',
            'Infrastructure & Active Directory',
        ],
        to: '/services/red-team',
        external: false,
    },
    {
        icon: <FiBook size={28} />,
        title: 'Akademi Siber',
        tag: 'self-paced platform',
        description:
            'Bahasa Indonesia learning platform with gamified labs. In partnership with REDAC7D.',
        items: [
            'Hands-on CTF Style Learning',
            'Full Bahasa Indonesia Content',
            'Free & Premium Modules',
            'Student-Friendly Pricing',
        ],
        to: 'https://akademi.siberlab.id/',
        external: true,
    },
];

const Services = () => {
    return (
        <section id="services" className="relative bg-[#05060a] text-white px-6 md:px-16 py-24 overflow-hidden">
            <div className="absolute inset-0 bg-dots opacity-30 pointer-events-none" />
            <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-red-900/20 blur-3xl pointer-events-none" />

            <div className="relative max-w-7xl mx-auto">
                <Reveal className="text-center mb-16">
                    <span className="pill mb-4 inline-flex">SERVICES</span>
                    <h2 className="font-display text-4xl md:text-5xl font-bold mb-4">
                        Four pillars. One mission.
                    </h2>
                    <p className="text-gray-400 max-w-2xl mx-auto text-lg">
                        Comprehensive cybersecurity capabilities tailored to your stack and your people.
                    </p>
                </Reveal>

                <CascadeGrid className="grid gap-6 md:grid-cols-2 lg:grid-cols-4" stagger={120}>
                    {services.map((service, index) => {
                        const cardInner = (
                            <div className={`card relative h-full p-6 group ${service.highlight ? 'ring-1 ring-red-500/40' : ''}`}>
                                <div className="absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-red-500/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                                <div className="flex items-center justify-between mb-5">
                                    <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-red-500/10 text-red-400 border border-red-500/30">
                                        {service.icon}
                                    </div>
                                    <span className="text-[10px] font-mono text-gray-500 uppercase tracking-widest">{service.tag}</span>
                                </div>
                                <h3 className="font-display text-xl font-semibold mb-2">{service.title}</h3>
                                <p className="text-gray-400 text-sm mb-4 leading-relaxed">{service.description}</p>
                                <ul className="text-sm text-gray-300 space-y-2 mb-6">
                                    {service.items.map((item, idx) => (
                                        <li key={idx} className="flex items-start gap-2">
                                            <span className="text-red-500 mt-1">▸</span>
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                                <span className="inline-flex items-center gap-1 text-red-400 group-hover:text-red-300 font-semibold text-sm transition-colors">
                                    Learn more <FiArrowUpRight />
                                </span>
                            </div>
                        );

                        return service.external ? (
                            <a key={index} href={service.to} target="_blank" rel="noreferrer" className="block h-full">
                                {cardInner}
                            </a>
                        ) : (
                            <Link key={index} to={service.to} className="block h-full">{cardInner}</Link>
                        );
                    })}
                </CascadeGrid>
            </div>
        </section>
    );
};

export default Services;
